import { getUrl } from '../../config';
import React from "react";
import styled from "@emotion/styled";
import { withTheme } from "@emotion/react";
import { darken } from "polished";
import { useTranslation } from "react-i18next";

import {
  Grid,
  InputBase,
  AppBar as MuiAppBar,
  IconButton as MuiIconButton,
  Toolbar,
  Box,
} from "@mui/material";

import { Menu as MenuIcon, VerticalSplit as VerticalSplitIcon } from "@mui/icons-material";

import NavbarNotificationsDropdown from "./NavbarNotificationsDropdown";
import NavbarMessagesDropdown from "./NavbarMessagesDropdown";
import NavbarLanguagesDropdown from "./NavbarLanguagesDropdown";
import NavbarChangelogIcon from "./NavbarChangelogIcon";
import NavbarUserDropdown from "./NavbarUserDropdown";
import NavbarErp from "./NavbarErp.js";
import NavbarBreadcrumb from "./NavbarBreadcrumb";
import useSettings from '../../hooks/useSettings';

const AppBar = styled(MuiAppBar)`
  background: ${(props) =>
    props.theme.palette.mode === "dark"
      ? "rgba(27, 38, 53, 0.85)"
      : "rgba(255, 255, 255, 0.85)"} !important;
  backdrop-filter: blur(12px);
  color: ${(props) => props.theme.header.color};
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.04);
  border-bottom: 1px solid ${(props) =>
    props.theme.palette.mode === "dark"
      ? "rgba(255, 255, 255, 0.06)"
      : "rgba(0, 0, 0, 0.06)"} !important;
`;

const IconButton = styled(MuiIconButton)`
  svg {
    width: 20px;
    height: 20px;
    transition: transform 0.2s ease-in-out;
  }
  border-radius: 8px;
  margin-right: 4px;
  padding: 8px;
  transition: all 0.2s ease-in-out;
  &:hover {
    background: ${(props) =>
      props.theme.palette.mode === "dark"
        ? "rgba(255, 255, 255, 0.06)"
        : "rgba(0, 0, 0, 0.04)"};
    color: ${(props) => props.theme.palette.primary.main};
    svg {
      transform: scale(1.05);
    }
  }
`;

const Search = styled.div`
  border-radius: 2px;
  background-color: ${(props) => props.theme.header.background};
  display: none;
  position: relative;
  width: 100%;

  &:hover {
    background-color: ${(props) => darken(0.05, props.theme.header.background)};
  }

  ${(props) => props.theme.breakpoints.up("md")} {
    display: block;
  }
`;

const SearchIconWrapper = styled.div`
  width: 50px;
  height: 100%;
  position: absolute;
  pointer-events: none;
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 22px;
    height: 22px;
  }
`;

const Input = styled(InputBase)`
  color: inherit;
  width: 100%;

  > input {
    color: ${(props) => props.theme.header.search.color};
    padding-top: ${(props) => props.theme.spacing(2.5)};
    padding-right: ${(props) => props.theme.spacing(2.5)};
    padding-bottom: ${(props) => props.theme.spacing(2.5)};
    padding-left: ${(props) => props.theme.spacing(12)};
    width: 160px;
  }
`;

const Navbar = ({ onDrawerToggle, onOpenToggle }) => {
  const { t } = useTranslation();
  const { settings, loading } = useSettings();
  const isSubFrame = window.self !== window.top;

  return (
    <React.Fragment>
      <AppBar position="sticky" elevation={0}>
        <Toolbar
          disableGutters
          sx={{
            minHeight: { xs: 60, sm: 72 },
            px: { xs: 1.5, sm: 3 },
          }}
        >
          <Grid container alignItems="center" wrap="nowrap">
            <Grid item sx={{ display: { xs: "block", md: "none" } }}>
              <IconButton
                color="inherit"
                aria-label="Open drawer"
                onClick={onDrawerToggle}
                size="large"
              >
                <MenuIcon />
              </IconButton>
            </Grid>
            <Grid item sx={{ display: { xs: "none", md: "block" } }}>
              <IconButton
                color="inherit"
                aria-label="Open drawer"
                onClick={onOpenToggle}
                size="large"
              >
                <MenuIcon />
              </IconButton>
            </Grid>

            <Grid
              item
              xs
              sx={{
                minWidth: 0,
                overflow: "hidden",
                display: { xs: "none", sm: "block" },
              }}
            >
              <NavbarBreadcrumb />
            </Grid>

            <Grid
              item
              sx={{
                flexShrink: 0,
                ml: { xs: "auto", md: 0 }, // pousse les éléments à droite sur mobile
              }}
            >
              <Box
                display="flex"
                alignItems="center"
                gap={0.5}
                sx={{
                  borderLeft: (theme) => `1px solid ${theme.palette.divider}`,
                  pl: 2.5,
                  ml: 1.5,
                }}
              >
                <NavbarErp />
                {!isSubFrame && (
                  <IconButton
                    color="inherit"
                    title="Travailler sur 2 ERP"
                    onClick={() => window.dispatchEvent(new CustomEvent('toggleSplitMode'))}
                  >
                    <VerticalSplitIcon />
                  </IconButton>
                )}
                <NavbarNotificationsDropdown />
                <NavbarLanguagesDropdown />
                <NavbarChangelogIcon />
                <NavbarUserDropdown />
                {!loading && settings?.logo && (
                  <Box
                    sx={{
                      ml: 2,
                      display: "flex",
                      alignItems: "center",
                      pl: 2,
                      borderLeft: (theme) => `1px solid ${theme.palette.divider}`,
                    }}
                  >
                    <img
                      src={getUrl() + `/images/upload/society/${settings.logo}`}
                      alt={settings.name || "Logo entreprise"}
                      style={{
                        height: "48px",
                        maxWidth: "130px",
                        objectFit: "contain",
                        filter:
                          settings?.erp_id === 1
                            ? "none"
                            : "brightness(0) invert(1)",
                      }}
                    />
                  </Box>
                )}
              </Box>
            </Grid>
          </Grid>
        </Toolbar>
      </AppBar>
    </React.Fragment>
  );
};

export default withTheme(Navbar);
